import { useEffect, useMemo, useRef, useState } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  addMonths,
  subMonths,
} from "date-fns";
import { ChevronLeft, ChevronRight, ArrowRight, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { useGetJournal } from "@workspace/api-client-react";
import { JOURNAL_LIBRARY_TAGS, coerceJournalTagForDisplay, type JournalLibraryTag } from "@/lib/journal-tags";

interface JournalEntry {
  id: string;
  title: string;
  tag: string;
  createdAt: string;
  coreInsight?: string | null;
}

const DOT_COLORS: Record<JournalLibraryTag, string> = {
  Health: "bg-sage-foreground/60",
  Finance: "bg-golden-foreground/60",
  "Self-care": "bg-blush-foreground/60",
  "Self-growth": "bg-lavender-foreground/60",
  Tech: "bg-sky-foreground/60",
  Other: "bg-[hsl(15_38%_42%/0.6)]",
};

const TAG_CHIP: Record<JournalLibraryTag, string> = {
  Health: "bg-sage/25 text-sage-foreground",
  Finance: "bg-golden/25 text-golden-foreground",
  "Self-care": "bg-blush/25 text-blush-foreground",
  "Self-growth": "bg-lavender/30 text-lavender-foreground",
  Tech: "bg-sky/25 text-sky-foreground",
  Other: "bg-muted text-muted-foreground",
};

/** Strip the light markdown the model emits so previews read as plain prose. */
function plain(text: string | null | undefined): string {
  return (text ?? "").replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1").trim();
}

export function JournalCalendar({
  journals,
  onVisibleMonthChange,
  onSelectedDateChange,
  restoreState,
}: {
  journals: JournalEntry[];
  /** Month/day to restore on mount. The calendar is unmounted when the user
   * switches to the Entries view, so without this a round-trip would silently
   * reset the browsed month — and with it the Insights period. */
  restoreState?: { month: Date; selectedDate: Date | null } | null;
  /** Fires whenever the browsed month changes (arrows), so a parent can scope
   * month-level stats to whatever month is actually on screen. */
  onVisibleMonthChange?: (month: Date) => void;
  /** Fires whenever the selected day changes (including back to null), so a
   * parent can scope week-level insights to the day the user is looking at. */
  onSelectedDateChange?: (date: Date | null) => void;
}) {
  const [, setLocation] = useLocation();

  /** Most recent day that holds an unfolding — the calendar opens there rather than blank. */
  const latestDate = useMemo(() => {
    if (journals.length === 0) return null;
    return journals.reduce<Date>((latest, j) => {
      const d = new Date(j.createdAt);
      return d > latest ? d : latest;
    }, new Date(journals[0].createdAt));
  }, [journals]);

  const [currentMonth, setCurrentMonthState] = useState<Date>(
    restoreState?.month ?? latestDate ?? new Date(),
  );
  const [selectedDate, setSelectedDateState] = useState<Date | null>(
    restoreState ? restoreState.selectedDate : latestDate,
  );

  const setCurrentMonth = (month: Date) => {
    setCurrentMonthState(month);
    onVisibleMonthChange?.(month);
  };
  const setSelectedDate = (date: Date | null) => {
    setSelectedDateState(date);
    onSelectedDateChange?.(date);
  };

  // Report the initial values once on mount, so the parent doesn't have to
  // duplicate the "open on latest entry" default to stay in sync.
  const didReportInitial = useRef(false);
  useEffect(() => {
    if (didReportInitial.current) return;
    didReportInitial.current = true;
    onVisibleMonthChange?.(currentMonth);
    onSelectedDateChange?.(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  // Full weeks so the grid keeps its shape and adjacent-month days stay visible.
  const gridDays = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end: endOfWeek(monthEnd, { weekStartsOn: 1 }),
  });

  const journalsOnDate = (date: Date) => journals.filter((j) => isSameDay(new Date(j.createdAt), date));
  const selectedJournals = selectedDate ? journalsOnDate(selectedDate) : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.85fr)_minmax(0,1fr)]">
      {/* ── Calendar ───────────────────────────────────────────────────── */}
      <div className="p-5 sm:p-6 lg:border-r lg:border-foreground/8">
        <div className="mb-5 flex items-center justify-between">
          <button
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            aria-label="Previous month"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-foreground/10 text-foreground/50 transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <h3 className="font-display text-lg font-medium text-foreground">
            {format(currentMonth, "MMMM yyyy")}
          </h3>
          <button
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            aria-label="Next month"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-foreground/10 text-foreground/50 transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-1 grid grid-cols-7">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div
              key={d}
              className="py-1 text-center text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/70"
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {gridDays.map((day) => {
            const inMonth = isSameMonth(day, currentMonth);
            const dayJournals = inMonth ? journalsOnDate(day) : [];
            // One dot per theme, so a day reads as its range rather than its volume.
            const themes = Array.from(
              new Set(dayJournals.map((j) => coerceJournalTagForDisplay(j.tag))),
            ).slice(0, 3);
            const isSelected = !!selectedDate && isSameDay(day, selectedDate);
            const isToday = isSameDay(day, new Date());

            if (!inMonth) {
              return (
                <div key={day.toISOString()} className="flex flex-col items-center py-1.5">
                  <span className="grid h-8 w-8 place-items-center text-sm text-muted-foreground/30">
                    {format(day, "d")}
                  </span>
                  <span className="mt-1 h-1.5" />
                </div>
              );
            }

            return (
              <button
                key={day.toISOString()}
                onClick={() => setSelectedDate(isSelected ? null : day)}
                aria-label={format(day, "EEEE, MMMM d")}
                aria-pressed={isSelected}
                className="group flex flex-col items-center py-1.5"
              >
                <span
                  className={cn(
                    "grid h-8 w-8 place-items-center rounded-full text-sm transition-colors",
                    isSelected
                      ? "bg-primary/12 font-semibold text-foreground"
                      : isToday
                        ? "font-semibold text-primary group-hover:bg-primary/[0.07]"
                        : dayJournals.length > 0
                          ? "font-medium text-foreground group-hover:bg-foreground/[0.05]"
                          : "text-foreground/35 group-hover:bg-foreground/[0.035]",
                  )}
                >
                  {format(day, "d")}
                </span>
                <span className="mt-1 flex h-1.5 items-center gap-[3px]">
                  {themes.map((theme) => (
                    <span key={theme} className={cn("h-1.5 w-1.5 rounded-full", DOT_COLORS[theme])} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>

        {/* Legend — present for decoding the dots, not for reading. */}
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-foreground/8 pt-4">
          {JOURNAL_LIBRARY_TAGS.map((tag) => (
            <div key={tag} className="flex items-center gap-1.5">
              <span className={cn("h-1.5 w-1.5 rounded-full", DOT_COLORS[tag])} />
              <span className="text-[11px] text-muted-foreground/80">{tag}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Selected day ───────────────────────────────────────────────── */}
      <div className="border-t border-foreground/8 p-5 sm:p-6 lg:border-t-0">
        {!selectedDate ? (
          <PanelMessage
            align="center"
            title="Pick a day"
            body="Days with a dot hold an unfolding. Choose one to read what surfaced."
          />
        ) : selectedJournals.length === 0 ? (
          <>
            <PanelDateLine date={selectedDate} />
            <PanelMessage
              title="A little space can be part of the process."
              body="When you're ready, your next unfolding will appear here."
            />
          </>
        ) : selectedJournals.length === 1 ? (
          <SingleUnfolding
            journal={selectedJournals[0]}
            date={selectedDate}
            onOpen={() => setLocation(`/journal/${selectedJournals[0].id}`)}
          />
        ) : (
          <>
            <PanelDateLine date={selectedDate} count={selectedJournals.length} />
            <div className="mt-4 space-y-2">
              {selectedJournals.map((j) => (
                <button
                  key={j.id}
                  onClick={() => setLocation(`/journal/${j.id}`)}
                  className="w-full rounded-2xl border border-foreground/8 bg-white/60 p-3.5 text-left transition-all hover:border-foreground/15 hover:bg-white"
                >
                  <p className="font-display text-[15px] font-medium leading-snug text-foreground">
                    {j.title}
                  </p>
                  <div className="mt-1.5 flex items-center gap-2">
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        DOT_COLORS[coerceJournalTagForDisplay(j.tag)],
                      )}
                    />
                    <span className="text-xs text-muted-foreground">
                      {coerceJournalTagForDisplay(j.tag)}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground/70">
                      {format(new Date(j.createdAt), "h:mm a")}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PanelDateLine({ date, count }: { date: Date; count?: number }) {
  const today = isSameDay(date, new Date());
  return (
    <div className="flex items-center gap-2">
      <p className="text-sm text-muted-foreground">{format(date, "EEEE, MMMM d")}</p>
      {today && (
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
          Today
        </span>
      )}
      {count != null && count > 1 && (
        <span className="ml-auto text-xs text-muted-foreground/70">{count} unfoldings</span>
      )}
    </div>
  );
}

function PanelMessage({
  title,
  body,
  align = "top",
}: {
  title: string;
  body: string;
  align?: "top" | "center";
}) {
  return (
    <div
      className={cn(
        "flex flex-col",
        align === "center" ? "h-full min-h-[180px] justify-center py-6" : "mt-4",
      )}
    >
      <p className="font-display text-base font-medium leading-snug text-foreground/75">{title}</p>
      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

/**
 * The list payload carries title/insight/tag; mood, imagery and the reflection
 * sections only exist on the full record, so it renders from the summary first
 * and fills the rest in when the detail arrives.
 */
function SingleUnfolding({
  journal,
  date,
  onOpen,
}: {
  journal: JournalEntry;
  date: Date;
  onOpen: () => void;
}) {
  const { data: detail } = useGetJournal(journal.id);

  const tag = coerceJournalTagForDisplay(journal.tag);
  const preview = plain(detail?.coreInsight ?? journal.coreInsight);
  const image = detail?.imageUrls?.[0] ?? detail?.illustrationUrls?.[0] ?? null;
  const mood = plain(detail?.mood);
  const nextSteps = detail?.actions?.length ?? 0;
  const insights = [detail?.coreInsight, detail?.whatSurfaced, detail?.reflectionPrompt, detail?.pullQuote]
    .filter((v) => typeof v === "string" && v.trim().length > 0).length;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2">
        <PanelDateLine date={date} />
        <span className="ml-auto shrink-0 text-xs text-muted-foreground/70">
          {format(new Date(journal.createdAt), "h:mm a")}
        </span>
      </div>

      <h3 className="mt-3 font-display text-xl font-medium leading-snug text-foreground">
        {journal.title}
      </h3>

      {image && (
        <img
          src={image}
          alt=""
          className="mt-3 h-32 w-full rounded-2xl object-cover"
          loading="lazy"
        />
      )}

      {preview && (
        <p className="mt-3 line-clamp-4 text-sm leading-relaxed text-foreground/65">{preview}</p>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className={cn("rounded-full px-2.5 py-1 text-[11px] font-medium", TAG_CHIP[tag])}>
          {tag}
        </span>
      </div>

      {(mood || nextSteps > 0) && (
        <div className="mt-4 flex items-center gap-6 border-t border-foreground/8 pt-4">
          {mood && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/70">
                Mood
              </p>
              <p className="mt-0.5 font-display text-base text-foreground">{mood}</p>
            </div>
          )}
          {nextSteps > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/70">
                Next steps
              </p>
              <p className="mt-0.5 font-display text-base text-foreground">{nextSteps}</p>
            </div>
          )}
        </div>
      )}

      {insights > 0 && (
        <div className="mt-4 flex items-center gap-2 rounded-full bg-primary/[0.06] px-3.5 py-2.5">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
          <span className="text-sm text-foreground/75">
            {insights} {insights === 1 ? "insight" : "insights"} uncovered
          </span>
        </div>
      )}

      {/* Primary action is Ink (brand style guide); violet stays reserved for Unfold. */}
      <Button onClick={onOpen} className="mt-4 w-full gap-2 rounded-[13px] text-sm">
        View unfolding
        <ArrowRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
